/**
 * Renderer tile execution service (S3) — the SINGLE fetch path. Every tile
 * result comes through `startTileExecution` (called only by `useTileResult`'s
 * queryFn); the scheduler never executes, it only triggers refetches.
 * The main-process policy is the authority: this service just composes the
 * query, ships the request, and handles the manual destructive-confirm
 * round-trip (the confirm flag is a separate IPC argument — the request type
 * carries no write-permission field).
 */
import type {
  BoundParams,
  DashboardParameter,
  DashboardTile,
  NoteTile,
} from '../types/dashboardTypes';
import type {
  ActiveDashboardFilter,
  TileExecutionRequest,
  TileRunResult,
} from '../types/dashboardRuntime';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import { hasLiveFiltersSlot, injectFiltersSlot, toWireFilters } from './crossFilter';
import { recordTileRunOutcome } from './dashboardRuntimeTelemetry';
import { deriveTileParameters } from './parameterBinding';

/** Renderer-suggested limits; the main process clamps to its own maxima. */
export const TILE_DEFAULT_LIMITS = {
  maxRows: 10_000,
  maxBytes: 32 * 1024 * 1024,
  timeoutMs: 30_000,
} as const;

export type QueryBackedTile = Exclude<DashboardTile, NoteTile>;

/**
 * The request query stays RAW (S10): `{{param}}` tokens and the
 * `{{dashboard_filters}}` slot travel to main untouched — the scheduleTrust
 * hash binds raw persisted SQL to the raw request query (S7-R1 invariant),
 * and main composes AFTER the policy decision. Typed values ride separately
 * on `request.parameters`; S11 cross-filter values ride the same way on
 * `request.dashboardFilters` (never SQL text). The renderer sends NO
 * composed SQL at all (C3-R2): main injects the slot itself for slot-less
 * tiles with the same pure `injectFiltersSlot` used here for the
 * participation test.
 */
export function buildFilterWire(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
  engine: string,
): Pick<TileExecutionRequest, 'dashboardFilters'> {
  const wire = toWireFilters(tile, filters);
  if (wire.length === 0) return {};
  if (hasLiveFiltersSlot(tile.source.sql, engine)) return { dashboardFilters: wire };
  // Callers pass filters through relevantFiltersForTile, whose participation
  // test includes composability — a failed injection here means state drifted
  // mid-flight; run unfiltered rather than lie with a broken predicate.
  if (injectFiltersSlot(tile.source.sql, engine) === null) return {};
  return { dashboardFilters: wire };
}

/**
 * Everything the renderer needs to derive this tile's wire parameters:
 * declared definitions, current values, and the engine (tokenizer syntax).
 * `null` = the surrounding surface has no dashboard parameters (dialog
 * preview, AI preview) — tiles using `{{param}}` there fail with a clear
 * main-side "unresolved placeholder" message.
 */
export type TileParamContext = {
  parameters: DashboardParameter[];
  values: BoundParams;
  engine: string;
};

/**
 * Destructive-confirm seam for manual runs. S6 wires the real dialog
 * (DangerousQueryDialog); until then manual destructive runs are declined.
 */
let dangerousConfirmer: (reason: string, query: string) => Promise<boolean> = async () => false;
export function setTileDangerousConfirmer(fn: typeof dangerousConfirmer): void {
  dangerousConfirmer = fn;
}

let runCounter = 0;

export function startTileExecution(
  tile: QueryBackedTile,
  paramContext: TileParamContext | null,
  filters: ActiveDashboardFilter[],
  opts: { trigger: TileExecutionRequest['trigger']; dashboardId: string },
): { executionId: string; promise: Promise<TileRunResult>; cancel(): void } {
  const executionId = `dash-${tile.id}-${Date.now()}-${++runCounter}`;

  // Derive at RUN time, not render time — fixed time-range windows resolve a
  // fresh `now` on every refresh. Unbound names fail fast, without IPC.
  let parameters: TileExecutionRequest['parameters'] = [];
  if (paramContext) {
    const derived = deriveTileParameters({
      sql: tile.source.sql,
      engine: paramContext.engine,
      parameters: paramContext.parameters,
      values: paramContext.values,
    });
    if (!derived.ok) {
      const message = `Missing dashboard parameter value${derived.missing.length > 1 ? 's' : ''}: ${derived.missing.join(', ')}`;
      return {
        executionId,
        promise: Promise.resolve({ ok: false, executionId, error: message }),
        cancel: () => {},
      };
    }
    parameters = derived.parameters;
  }

  const request: TileExecutionRequest = {
    connectionId: tile.connectionId,
    database: tile.database,
    // RAW SQL — the S7-R1 trust-hash invariant; filters/params ride as data.
    query: tile.source.sql,
    parameters,
    ...(paramContext ? buildFilterWire(tile, filters, paramContext.engine) : {}),
    ...TILE_DEFAULT_LIMITS,
    trigger: opts.trigger,
    dashboardId: opts.dashboardId,
    tileId: tile.id,
  };
  const promise = run(request, executionId);
  // Release-health aggregation — every executed run's outcome increments a
  // local counter (flushed on an interval / tab close), NEVER a per-run
  // analytics event. Tapping here keeps the single-fetch-path invariant: the
  // summary sees exactly what tiles actually ran.
  void promise.then((result) =>
    recordTileRunOutcome(
      paramContext?.engine ?? getEngineForConnection(tile.connectionId),
      opts.trigger,
      result,
    ),
  );
  return {
    executionId,
    promise,
    cancel: () => void window.electronAPI?.dashboards?.cancelTileRun(executionId).catch(() => {}),
  };
}

async function run(request: TileExecutionRequest, executionId: string): Promise<TileRunResult> {
  const bridge = window.electronAPI?.dashboards;
  if (!bridge?.runTile) {
    return { ok: false, executionId, error: 'Dashboards API is unavailable' };
  }
  const first = await bridge.runTile(request, executionId, false);
  if (first.kind === 'result') return first.result;
  // Manual destructive run: the existing confirm flow, every time.
  const confirmed = await dangerousConfirmer(first.reason, request.query);
  if (!confirmed) {
    return { ok: false, executionId, error: 'Run cancelled — confirmation declined' };
  }
  const second = await bridge.runTile(request, executionId, true);
  return second.kind === 'result'
    ? second.result
    : { ok: false, executionId, error: 'Unexpected repeated confirmation request' };
}
